/**
 * "Optimize route" dialog (tracker item 41, phase 3): one technician's day
 * from POST /dispatch/optimize-day. Shows the current and the proposed visit
 * order (numbered) with each job's old and new start, the drive into each
 * stop, total driving before and after and the minutes saved. "Keep time"
 * re-asks with that job pinned. "Apply" writes every change in one call
 * (all or nothing); a 409 means someone changed the day meanwhile, and the
 * page refreshes the board. Office roles only (the dispatch page is
 * route-gated).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { primaryButtonClass, secondaryButtonClass } from './formStyles';
import { formatDayHeading, formatTime } from '../pages/dispatchUi';
import {
  applyOptimization,
  canToggleKeep,
  optimizeOutcomeText,
  pinReasonText,
  timeChangeText,
  toggledPins,
  travelInText,
  travelSummaryText,
} from '../pages/optimizeUi';
import { Toast } from './Toast';

interface OptimizeDayDialogProps {
  userId: string;
  technicianLabel: string;
  date: string;
  api: Pick<IDispatchApi, 'optimizeDay' | 'applyDayOptimization'>;
  /** The changes were written */
  onApplied: (result: types.ApplyDayOptimizationResult, preview: types.OptimizeDayResult) => void;
  /** 409: someone changed the day; nothing was written */
  onChanged: () => void;
  onClose: () => void;
}

export function OptimizeDayDialog({ userId, technicianLabel, date, api, onApplied, onChanged, onClose }: OptimizeDayDialogProps) {
  const [pins, setPins] = useState<string[]>([]);
  const [result, setResult] = useState<types.OptimizeDayResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await api.optimizeDay({ userId, date, ...(pins.length ? { pinnedJobIds: pins } : {}) }));
    } catch (err) {
      setResult(null);
      setError(getErrorMessage(err, 'Could not optimize this day'));
    } finally {
      setLoading(false);
    }
  }, [api, userId, date, pins]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    return () => origin?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const apply = async () => {
    if (!result) return;
    setApplying(true);
    setError(null);
    try {
      const outcome = await applyOptimization(api, result);
      if (outcome.status === 'applied') onApplied(outcome.result, result);
      else onChanged();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not apply the new order'));
    } finally {
      setApplying(false);
    }
  };

  const currentById = new Map((result?.jobs ?? []).map((j) => [j.jobId, j]));
  const unit = result?.distanceUnit ?? 'MI';
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="optimize-day-title"
        className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:max-w-2xl sm:rounded-lg">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 id="optimize-day-title" className="text-lg font-semibold text-gray-900">Optimize route</h2>
            <p className="break-words text-sm text-gray-600">{technicianLabel} · {formatDayHeading(date)}</p>
          </div>
          <button ref={closeButton} type="button" className={secondaryButtonClass} onClick={onClose}>Close</button>
        </div>

        {error && <Toast kind="error">{error}</Toast>}
        {loading && <p role="status" className="mt-4 text-sm text-gray-600">Finding a shorter route…</p>}
        {!loading && result && (
          <>
            <div className="mt-3 rounded bg-gray-50 p-3 text-sm" data-optimize-summary>
              <p className="font-semibold text-gray-900">{optimizeOutcomeText(result)}</p>
              <p className="text-gray-700">
                {travelSummaryText(result)}
                {result.improved && result.current.dayEnd && result.proposed.dayEnd && (
                  <> · Day ends {formatTime(result.current.dayEnd)} → {formatTime(result.proposed.dayEnd)}</>
                )}
              </p>
            </div>

            {result.jobs.length > 0 && (
              <ol className="mt-3 space-y-2" aria-label="Proposed order">
                {result.jobs.map((job) => {
                  const toggleable = canToggleKeep(job);
                  return (
                    <li key={job.jobId} data-optimize-job={job.jobId}
                      className={`rounded border p-3 text-sm ${job.changed ? 'border-blue-300 bg-blue-50/40' : 'border-gray-200'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900">
                            <span aria-label={`Stop ${job.newPosition}`}>{job.newPosition}.</span>{' '}
                            {timeChangeText(job)}
                            {job.oldPosition !== job.newPosition && <span className="ml-1 text-xs font-normal text-gray-500">(was {job.oldPosition})</span>}
                          </div>
                          <div className="break-words text-gray-900">{job.customerName || 'Customer'}</div>
                          <div className="break-words text-xs text-gray-600">{job.address}</div>
                          <div className="text-xs text-gray-600">{travelInText(job, unit)}</div>
                          {job.arrivalWindow && (
                            <div className="text-xs text-gray-600">Arrival window {formatTime(job.arrivalWindow.earliest)} - {formatTime(job.arrivalWindow.latest)}</div>
                          )}
                          {job.pinReason && job.pinReason !== 'KEPT' && <div className="text-xs font-medium text-gray-700">{pinReasonText(job.pinReason)}: keeps its time</div>}
                        </div>
                        {toggleable && (
                          <label className="inline-flex shrink-0 items-center gap-2 text-xs text-gray-700">
                            <input type="checkbox" checked={pins.includes(job.jobId)} disabled={loading || applying}
                              onChange={() => setPins((current) => toggledPins(current, job.jobId))} />
                            Keep time
                          </label>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            {result.leftOut.length > 0 && (
              <p className="mt-3 text-xs text-gray-600">
                Not in the route (no start time): {result.leftOut.map((j) => j.customerName || j.jobId).join(', ')}.
              </p>
            )}
            {result.conflicts.length > 0 && (
              <p className="mt-2 text-xs text-red-700">
                Warnings remain on: {result.conflicts.map((c) => currentById.get(c.jobId)?.customerName ?? c.jobId).join(', ')}.
              </p>
            )}
            <p className="mt-3 text-xs text-gray-500">
              {result.unknownLocations > 0 || !result.depotKnown ? 'Some places have no map location; their drives are estimated. ' : ''}
              Travel is estimated from straight-line distance, starting at the company address. Map data © OpenStreetMap contributors.
            </p>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" className={secondaryButtonClass} onClick={onClose}>Cancel</button>
              <button type="button" className={primaryButtonClass} disabled={!result.improved || result.changes.length === 0 || applying} onClick={apply}>
                {applying ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
